/**
 * useLocalChessGame - Client-side chess game state management
 *
 * Manages a chess game entirely on the client using chess.js.
 * Combined with useStockfish for AI opponent.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { Chess } from 'chess.js';
import { ChessGame, ChessColor, ChessPiece } from '../types';
import { useStockfish, DifficultyLevel, DIFFICULTY_PRESETS } from './useStockfish';

// Local move type for tracking game history
interface ChessMove {
    from: string;
    to: string;
    piece: string;
    color: ChessColor;
    promotion?: string;
}

export interface UseLocalChessGameOptions {
    playerColor?: 'white' | 'black';
    difficulty?: DifficultyLevel;
    onGameOver?: (result: 'win' | 'lose' | 'draw', reason: string) => void;
}

export interface UseLocalChessGameReturn {
    game: ChessGame;
    isPlayerTurn: boolean;
    isThinking: boolean;
    isEngineReady: boolean;
    difficulty: DifficultyLevel;
    setDifficulty: (level: DifficultyLevel) => void;
    makeMove: (from: { row: number; col: number }, to: { row: number; col: number }) => boolean;
    resetGame: () => void;
    undoMove: () => void;
    gameStatus: 'playing' | 'checkmate' | 'stalemate' | 'draw';
    isCheck: boolean;
}

// Convert chess.js board to our 2D array format
function chessJsBoardToArray(chess: Chess): ChessPiece[][] {
    const board: ChessPiece[][] = [];
    for (let rank = 8; rank >= 1; rank--) {
        const row: ChessPiece[] = [];
        for (let file = 0; file < 8; file++) {
            const square = `${'abcdefgh'[file]}${rank}` as any;
            const piece = chess.get(square);
            if (piece) {
                // chess.js uses { type: 'p', color: 'w' } format
                const symbol = piece.color === 'w'
                    ? piece.type.toUpperCase()
                    : piece.type.toLowerCase();
                row.push(symbol as ChessPiece);
            } else {
                row.push(null);
            }
        }
        board.push(row);
    }
    return board;
}

// Convert our coordinates to algebraic notation
function coordsToAlgebraic(row: number, col: number, playerColor: 'white' | 'black'): string {
    // If playing as black, board is flipped
    const actualRow = playerColor === 'black' ? row : 7 - row;
    const actualCol = playerColor === 'black' ? 7 - col : col;
    const file = 'abcdefgh'[actualCol];
    const rank = actualRow + 1;
    return `${file}${rank}`;
}

export function useLocalChessGame(options: UseLocalChessGameOptions = {}): UseLocalChessGameReturn {
    const {
        playerColor = 'white',
        difficulty: initialDifficulty = 'medium',
        onGameOver
    } = options;

    const [chess] = useState(() => new Chess());
    const [boardState, setBoardState] = useState<ChessPiece[][]>(() => chessJsBoardToArray(chess));
    const [moves, setMoves] = useState<ChessMove[]>([]);
    const [gameStatus, setGameStatus] = useState<'playing' | 'checkmate' | 'stalemate' | 'draw'>('playing');

    const {
        isReady: isEngineReady,
        isThinking,
        difficulty,
        setDifficulty,
        getBestMove
    } = useStockfish({ difficulty: initialDifficulty });

    // Determine whose turn it is
    const isWhiteTurn = chess.turn() === 'w';
    const isPlayerTurn = (playerColor === 'white' && isWhiteTurn) || (playerColor === 'black' && !isWhiteTurn);

    // Check if in check
    const isCheck = chess.isCheck();

    // Build game object compatible with ChessContainer
    const game: ChessGame = useMemo(() => ({
        id: 'local',
        gameName: 'vs Computer',
        state: gameStatus === 'playing' ? 'ongoing' : 'completed',
        board: boardState,
        moves: moves,
        players: [
            {
                id: 'player',
                color: playerColor === 'white' ? ChessColor.PLAIN : ChessColor.CHARCOAL,
                joinKey: '',
                capturedPieces: [] as string[],
                moves: [],
                timeLeft: 0,
                disconnected: false,
                socketId: undefined
            },
            {
                id: 'stockfish',
                color: playerColor === 'white' ? ChessColor.CHARCOAL : ChessColor.PLAIN,
                joinKey: '',
                capturedPieces: [] as string[],
                moves: [],
                timeLeft: 0,
                disconnected: false,
                socketId: 'AI-stockfish'
            }
        ],
        currentPlayer: isPlayerTurn ? 'player' : 'stockfish',
        winnerId: null,
        endReason: null,
        castling: 'KQkq', // All castling rights available initially
        moveNumber: moves.length,
        autoplay: true,
        lastActivity: Date.now()
    }), [boardState, moves, gameStatus, playerColor, isPlayerTurn]);

    // Make a move (player's move)
    const makeMove = useCallback((from: { row: number; col: number }, to: { row: number; col: number }): boolean => {
        if (!isPlayerTurn || gameStatus !== 'playing') {
            return false;
        }

        const fromSquare = coordsToAlgebraic(from.row, from.col, playerColor);
        const toSquare = coordsToAlgebraic(to.row, to.col, playerColor);

        try {
            // Try to make the move (chess.js validates legality)
            const result = chess.move({
                from: fromSquare,
                to: toSquare,
                promotion: 'q' // Always promote to queen for simplicity
            });

            if (result) {
                const newMove: ChessMove = {
                    from: fromSquare,
                    to: toSquare,
                    piece: result.piece,
                    color: result.color === 'w' ? ChessColor.PLAIN : ChessColor.CHARCOAL,
                    promotion: result.promotion
                };

                setMoves(prev => [...prev, newMove]);
                setBoardState(chessJsBoardToArray(chess));

                // Check for game over
                if (chess.isCheckmate()) {
                    setGameStatus('checkmate');
                    onGameOver?.('win', 'checkmate');
                } else if (chess.isStalemate()) {
                    setGameStatus('stalemate');
                    onGameOver?.('draw', 'stalemate');
                } else if (chess.isDraw()) {
                    setGameStatus('draw');
                    onGameOver?.('draw', 'draw');
                }

                return true;
            }
        } catch (e) {
            console.log('Invalid move:', fromSquare, '->', toSquare);
        }

        return false;
    }, [chess, isPlayerTurn, gameStatus, playerColor, onGameOver]);

    // AI makes a move
    useEffect(() => {
        if (!isPlayerTurn && isEngineReady && gameStatus === 'playing' && !isThinking) {
            const makeAIMove = async () => {
                const fen = chess.fen();
                const bestMove = await getBestMove(fen);

                if (bestMove && gameStatus === 'playing') {
                    try {
                        const result = chess.move({
                            from: bestMove.from,
                            to: bestMove.to,
                            promotion: bestMove.promotion || 'q'
                        });

                        if (result) {
                            const newMove: ChessMove = {
                                from: bestMove.from,
                                to: bestMove.to,
                                piece: result.piece,
                                color: result.color === 'w' ? ChessColor.PLAIN : ChessColor.CHARCOAL,
                                promotion: result.promotion
                            };

                            setMoves(prev => [...prev, newMove]);
                            setBoardState(chessJsBoardToArray(chess));

                            // Check for game over
                            if (chess.isCheckmate()) {
                                setGameStatus('checkmate');
                                onGameOver?.('lose', 'checkmate');
                            } else if (chess.isStalemate()) {
                                setGameStatus('stalemate');
                                onGameOver?.('draw', 'stalemate');
                            } else if (chess.isDraw()) {
                                setGameStatus('draw');
                                onGameOver?.('draw', 'draw');
                            }
                        }
                    } catch (e) {
                        console.error('AI move failed:', e);
                    }
                }
            };

            // Small delay to make AI feel more natural
            const timer = setTimeout(makeAIMove, 300);
            return () => clearTimeout(timer);
        }
    }, [isPlayerTurn, isEngineReady, gameStatus, isThinking, chess, getBestMove, onGameOver]);

    // Reset game
    const resetGame = useCallback(() => {
        chess.reset();
        setBoardState(chessJsBoardToArray(chess));
        setMoves([]);
        setGameStatus('playing');
    }, [chess]);

    // Undo last move (undoes both player and AI move)
    const undoMove = useCallback(() => {
        if (moves.length >= 2) {
            chess.undo(); // Undo AI move
            chess.undo(); // Undo player move
            setBoardState(chessJsBoardToArray(chess));
            setMoves(prev => prev.slice(0, -2));
            setGameStatus('playing');
        }
    }, [chess, moves.length]);

    return {
        game,
        isPlayerTurn,
        isThinking,
        isEngineReady,
        difficulty,
        setDifficulty,
        makeMove,
        resetGame,
        undoMove,
        gameStatus,
        isCheck
    };
}

export default useLocalChessGame;
